import * as React from "react"
import { useNavigate } from "react-router-dom"
import { useAuthStore } from "@/store"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { api } from "@/lib/api"
import { User, Mail, Shield, Calendar, Save, Loader2, ArrowLeft, Lock } from "lucide-react"

function ProfilePage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const [saving, setSaving] = React.useState(false)
  const [success, setSuccess] = React.useState("")
  const [error, setError] = React.useState("")

  const [formData, setFormData] = React.useState({
    username: user?.username || "",
    email: user?.email || "",
  })

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setError("")
    setSuccess("")
  }

  const handleSave = async () => {
    setSaving(true)
    setError("")
    setSuccess("")

    try {
      const response = await api.put("/auth/profile", {
        username: formData.username,
        email: formData.email,
      })
      
      if (response.success) {
        setUser({ ...user, username: formData.username, email: formData.email })
        setSuccess("Profile updated successfully")
      } else {
        setError(response.error || "Failed to update profile")
      }
    } catch (err) {
      setError(err.message || "Failed to update profile")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon-sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-[28px] font-bold tracking-tight text-[hsl(var(--text-primary))]">
            Profile
          </h1>
          <p className="text-[14px] text-[hsl(var(--text-secondary))]">
            Manage your account settings
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Profile Info */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>Update your personal details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="text-[13px] font-medium text-[hsl(var(--text-secondary))]">
                  Username
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
                  <Input
                    value={formData.username}
                    onChange={(e) => handleChange("username", e.target.value)}
                    className="pl-9"
                    placeholder="Enter username"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[13px] font-medium text-[hsl(var(--text-secondary))]">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="pl-9"
                    placeholder="Enter email"
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="rounded-[var(--radius-md)] bg-[hsl(var(--status-error-bg))] p-3 text-[13px] text-[hsl(var(--status-error))]">
                {error}
              </div>
            )}

            {success && (
              <div className="rounded-[var(--radius-md)] bg-[hsl(var(--status-success-bg))] p-3 text-[13px] text-[hsl(var(--status-success))]">
                {success}
              </div>
            )}

            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Changes
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card>
          <CardHeader>
            <CardTitle>Account</CardTitle>
            <CardDescription>Your account details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 rounded-[var(--radius-md)] bg-[hsl(var(--bg-hover))] p-3">
              <Shield className="h-5 w-5 text-[hsl(var(--brand-500))]" />
              <div>
                <p className="text-[13px] font-medium text-[hsl(var(--text-primary))]">Role</p>
                <p className="text-[12px] text-[hsl(var(--text-muted))] capitalize">{user?.role || "Admin"}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-[var(--radius-md)] bg-[hsl(var(--bg-hover))] p-3">
              <Calendar className="h-5 w-5 text-[hsl(var(--brand-500))]" />
              <div>
                <p className="text-[13px] font-medium text-[hsl(var(--text-primary))]">Member Since</p>
                <p className="text-[12px] text-[hsl(var(--text-muted))]">January 2024</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate("/change-password")}
            >
              <Lock className="mr-2 h-4 w-4" />
              Change Password
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default ProfilePage
